export interface SupabaseEnv {
  readonly supabaseUrl: string;
  readonly supabaseKey: string;
  readonly appKey: string;
  readonly workspaceSlug: string | null;
}

export class EnvConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EnvConfigError';
  }
}

let cached: SupabaseEnv | null = null;

/**
 * Which company (workspace) this browser is looking at.
 *
 * TWO-COMPANY SEPARATION: every table is scoped by workspace_id and RLS checks membership, so
 * two companies are two workspaces and their data can never mix. What used to be missing was a
 * way to *choose* one: the slug was baked in at build time, so one deployment could only ever
 * open one company. It is now resolved per browser, in this order:
 *
 *   1. ?company=<slug> (or ?workspace=<slug>) in the URL  — each company gets its own link,
 *      which is what you bookmark, share and put behind its own domain.
 *   2. the slug this browser last switched to (localStorage).
 *   3. VITE_WORKSPACE_SLUG from the build — still honoured for single-company deployments.
 *   4. nothing: the user's first (or only) membership wins.
 *
 * The server never trusts any of this: resolveWorkspace() only returns a workspace the signed-in
 * user is an active member of, and RLS re-checks every row afterwards.
 */
export const COMPANY_LS_KEY = 'LUVIA_TOUR:work:company';
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}$/;

function readSlug(value: string | null | undefined): string | null {
  const slug = (value ?? '').trim().toLowerCase();
  return SLUG_RE.test(slug) ? slug : null;
}

export function companySlugFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const q = new URLSearchParams(window.location.search);
  return readSlug(q.get('company') ?? q.get('workspace'));
}

function companySlugFromStorage(): string | null {
  try {
    return readSlug(window.localStorage.getItem(COMPANY_LS_KEY));
  } catch {
    return null;
  }
}

/** Remembers the chosen company and puts it in the URL, so the link is shareable. */
export function rememberCompanySlug(slug: string | null): void {
  try {
    if (slug && SLUG_RE.test(slug)) window.localStorage.setItem(COMPANY_LS_KEY, slug);
    else window.localStorage.removeItem(COMPANY_LS_KEY);
  } catch {
    // private mode / blocked storage: the URL parameter still carries the choice
  }
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  const url = new URL(window.location.href);
  if (slug) url.searchParams.set('company', slug);
  else url.searchParams.delete('company');
  window.history.replaceState(window.history.state, '', url.toString());
}

/** A shareable link that opens this dashboard on one specific company. */
export function companyUrl(slug: string): string {
  if (typeof window === 'undefined') return `?company=${encodeURIComponent(slug)}`;
  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.set('company', slug);
  return url.toString();
}

/** The company this browser should open, highest-priority source first. */
export function preferredCompanySlug(): string | null {
  return companySlugFromUrl() ?? companySlugFromStorage() ?? getEnv().workspaceSlug;
}

/**
 * Build-time configuration (Vite inlines import.meta.env.VITE_* during `vite build`).
 *
 * EMPLOYEE-PC SYNC FIX: the backend address used to be typed into Settings and kept in
 * the admin browser's localStorage, so employee PCs never knew where the server was.
 * It is now compiled into the bundle from Cloudflare Pages environment variables, so
 * every PC that opens the site talks to the same Supabase project with zero setup.
 */
export function getEnv(): SupabaseEnv {
  if (cached) return cached;

  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL ?? '').trim().replace(/\/+$/, '');
  const supabaseKey = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '').trim();
  const appKey = (import.meta.env.VITE_APP_KEY ?? '').trim() || 'work';
  const workspaceSlug = (import.meta.env.VITE_WORKSPACE_SLUG ?? '').trim() || null;

  const missing: string[] = [];
  if (!supabaseUrl) missing.push('VITE_SUPABASE_URL');
  if (!supabaseKey) missing.push('VITE_SUPABASE_PUBLISHABLE_KEY');
  if (missing.length > 0) {
    throw new EnvConfigError(
      `Missing environment variable(s): ${missing.join(', ')}. ` +
        'Set them in .env.local for development or in Cloudflare Pages → Settings → Variables and Secrets, then rebuild.',
    );
  }
  if (!/^https:\/\/[^/]+$/.test(supabaseUrl) && !/^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(supabaseUrl)) {
    throw new EnvConfigError(`VITE_SUPABASE_URL must look like https://<project-ref>.supabase.co (got "${supabaseUrl}").`);
  }
  if (!/^[a-z0-9][a-z0-9-]{1,62}$/.test(appKey)) {
    throw new EnvConfigError(`VITE_APP_KEY must be lowercase letters, digits or dashes (got "${appKey}").`);
  }

  cached = { supabaseUrl, supabaseKey, appKey, workspaceSlug };
  return cached;
}
